import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, FileText, FilePlus2, Users, FolderKanban, Layers, MessagesSquare, Settings, LogOut, Search, Bell, Globe, Menu, X, Building2, CreditCard, TrendingUp, Receipt, FileBarChart, Plug, Crown, Sun, Moon, Volume2, VolumeX, ShieldCheck, UserPlus, Package, Sparkles, ImageIcon } from "lucide-react";
import { Logo, LogoBrand, LogoCompact } from "../components/Brand";
import DemoModeBanner from "../components/DemoModeBanner";
import { useApp, API } from "../AppContext";
import { useSubscription } from "../hooks/useSubscription";
import CommandPalette from "../components/CommandPalette";
import AIGuide from "../components/AIGuide";
import SupportBubble from "../components/SupportBubble";
import ProgereEcosystemMenu from "../components/ProgereEcosystemMenu";
import ProgereLegalFooter from "../components/ProgereLegalFooter";
import ProgereVersionBadge from "../components/ProgereVersionBadge";
import ProgereMobileTabBar, { ProgereInstallPWABanner } from "../components/ProgereMobileTabBar";
import { CURRENT_MODULE_CODE, CURRENT_MODULE_NAME } from "../config/progere-modules";
import { useTheme } from "../hooks/useTheme";

export default function AppLayout() {
  const { t, lang, setLang, logout, user } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("progere_sound") !== "off");
  const { hasAccess: hasSubscription } = useSubscription();
  const userInitials = (user?.name || user?.email || "U")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join("").toUpperCase() || "U";
  const toggleSound = () => {
    setSoundOn((s) => { const v = !s; try { localStorage.setItem("progere_sound", v ? "on" : "off"); } catch {} return v; });
  };

  // Global keyboard shortcuts: ⌘K / Ctrl+K → palette · N → new quote · / → palette
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      } else if (!inField && e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (!inField && e.key.toLowerCase() === "n") {
        nav("/app/quotes/new");
      } else if (e.key === "Escape" && paletteOpen) {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nav, paletteOpen]);

  const refreshUnread = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/erp-leads/unread-count`);
      setUnread(data.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!hasSubscription) { setUnread(0); return; }
    refreshUnread();
    const id = setInterval(refreshUnread, 30000);
    return () => clearInterval(id);
  }, [loc.pathname, hasSubscription]);

  const allLinks = [
    { to: "/app", end: true, icon: LayoutDashboard, label: t.sidebar.dashboard, id: "dashboard", tone: "gold" },
    { to: "/app/analytics", icon: TrendingUp, label: lang === "fr" ? "Analytique" : "Analytics", id: "analytics", tone: "gold" },
    { to: "/app/quotes/new", icon: FilePlus2, label: t.sidebar.new_quote, id: "new-quote", tone: "red" },
    { to: "/app/quotes/new/vision", icon: Sparkles, label: lang === "fr" ? "Vision IA" : "Vision AI", id: "vision-ai", tone: "gold" },
    { to: "/app/vision/history", icon: ImageIcon, label: lang === "fr" ? "Historique Vision" : "Vision history", id: "vision-history", tone: "gold" },
    { to: "/app/quotes", icon: FileText, label: t.sidebar.quotes, id: "quotes", tone: "red" },
    { to: "/app/invoices", icon: Receipt, label: lang === "fr" ? "Factures" : "Invoices", id: "invoices", tone: "gold" },
    { to: "/app/reports", icon: FileBarChart, label: lang === "fr" ? "Rapports TPS/TVQ" : "Tax reports", id: "reports", tone: "red" },
    { to: "/app/clients", icon: Users, label: t.sidebar.clients, id: "clients", tone: "gold" },
    { to: "/app/projects", icon: FolderKanban, label: t.sidebar.projects, id: "projects", tone: "red" },
    { to: "/app/templates", icon: Layers, label: t.sidebar.templates, id: "templates", tone: "gold" },
    { to: "/app/assistant", icon: MessagesSquare, label: t.sidebar.assistant, id: "assistant", tone: "red" },
    { to: "/app/team", icon: UserPlus, label: lang === "fr" ? "Équipe" : "Team", id: "team", tone: "gold" },
    { to: "/app/erp-leads", icon: Building2, label: t.sidebar.erp_leads, id: "erp-leads", badge: unread, requiresSub: true, tone: "red" },
    { to: "/app/billing", icon: CreditCard, label: lang === "fr" ? "Abonnement" : "Subscription", id: "billing", tone: "gold" },
    { to: "/app/integrations", icon: Plug, label: lang === "fr" ? "Intégrations API" : "API Integrations", id: "integrations", requiresSub: true, tone: "red" },
    { to: "/app/settings", icon: Settings, label: t.sidebar.settings, id: "settings", tone: "gold" },
    { to: "/app/admin/releases", icon: Package, label: lang === "fr" ? "Versions & MAJ" : "Versions & updates", id: "admin-releases", adminOnly: true, tone: "red" },
  ];
  const links = allLinks.filter((l) => (!l.requiresSub || hasSubscription) && (!l.adminOnly || user?.role === "admin"));

  // Close mobile drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const SidebarContent = () => (
    <>
      {/* Header — hexagonal icon + PROGÈRE DEVIS AI label with "MODULE PROGÈRE ERP" tag */}
      <div className="px-2 mb-5 flex items-center justify-between">
        <div className="inline-flex items-center gap-2.5 select-none">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(192,0,0,0.15)", border: "1px solid rgba(192,0,0,0.35)" }}>
            <img src={`${process.env.PUBLIC_URL || ""}/progere-icon.png`} alt="PROGÈRE" style={{ width: 22, height: 22 }} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-black text-[15px] tracking-tight">
              <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
              <span className="ml-1" style={{ color: "#F5F5F5" }}>DEVIS AI</span>
            </div>
            <div className="text-[8.5px] font-bold tracking-[0.22em] uppercase mt-0.5"
              style={{ color: "rgba(212,175,55,0.6)" }}>
              MODULE PROGÈRE ERP
            </div>
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden p-2 rounded-full hover:bg-white/5" data-testid="close-mobile-menu">
          <X size={20} color="#F5F5F5" />
        </button>
      </div>

      {/* MODULE ACTIF card (identical to PROGÈRE PUNCH) */}
      <div className="mx-1 mb-5 rounded-2xl px-4 py-3"
        style={{
          background: "linear-gradient(180deg, rgba(24,24,24,0.95), rgba(11,11,11,0.95))",
          border: "1px solid rgba(212,175,55,0.3)",
          boxShadow: "0 8px 22px -8px rgba(212,175,55,0.25), inset 0 1px 0 rgba(212,175,55,0.1)",
        }}
        data-testid="module-active-badge">
        <div className="text-[9px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: "rgba(212,175,55,0.75)" }}>
          {lang === "fr" ? "Module actif" : "Active module"}
        </div>
        <div className="font-heading font-black text-[14px] tracking-tight leading-none">
          <span style={{ color: "#D4AF37" }}>PROGÈRE</span>
          <span className="ml-1" style={{ color: "#F5F5F5" }}>DEVIS AI</span>
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 mb-2" style={{ color: "rgba(245,245,245,0.35)" }}>
        {lang === "fr" ? "PROGÈRE DEVIS AI" : "PROGÈRE DEVIS AI"}
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto pr-1" data-testid="sidebar-nav">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid={`sidebar-link-${l.id}`}>
            <l.icon size={17} color={l.tone === "red" ? "#EF4444" : "#D4AF37"} />
            <span className="flex-1">{l.label}</span>
            {l.badge > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#C00000", color: "#F5F5F5" }} data-testid={`badge-${l.id}`}>
                {l.badge}
              </span>
            )}
          </NavLink>
        ))}

        <div className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 mt-6 mb-2" style={{ color: "rgba(245,245,245,0.35)" }}>
          {lang === "fr" ? "Écosystème" : "Ecosystem"}
        </div>
        <a href="https://progere.ca" target="_blank" rel="noopener noreferrer"
          className="sidebar-link"
          style={{ position: "relative" }}
          data-testid="sidebar-ecosystem-link">
          <Crown size={17} color="#D4AF37" />
          <span className="flex-1">{lang === "fr" ? "Retour à PROGÈRE" : "Back to PROGÈRE"}</span>
          <span className="text-[9px]" style={{ color: "rgba(212,175,55,0.6)" }}>↗</span>
        </a>
      </nav>

      {/* Version badge — bas de la sidebar */}
      <div className="pt-3 mt-2 border-t" style={{ borderColor: "rgba(212,175,55,0.1)" }}>
        <ProgereVersionBadge moduleCode={CURRENT_MODULE_CODE} moduleName={CURRENT_MODULE_NAME} />
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "transparent" }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col px-4 py-6 border-r" style={{ borderColor: "rgba(212,175,55,0.1)", background: "rgba(11,11,11,0.85)", backdropFilter: "blur(20px)" }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
               onClick={() => setMobileOpen(false)} data-testid="mobile-backdrop" />
          <aside className="lg:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] z-50 flex flex-col px-4 py-6 border-r"
                 style={{ borderColor: "rgba(212,175,55,0.18)", background: "transparent" }}>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar — PROGÈRE PUNCH exact style */}
        <header className="sticky top-0 z-30 px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 border-b"
          style={{ borderColor: "rgba(212,175,55,0.08)", background: "rgba(11,11,11,0.85)", backdropFilter: "blur(20px)" }}>
          <button onClick={() => setMobileOpen(true)} className="lg:hidden btn-ghost text-xs p-2 flex-shrink-0" data-testid="open-mobile-menu" aria-label="Menu">
            <Menu size={18} />
          </button>
          <div className="lg:hidden flex-shrink-0">
            <LogoCompact />
          </div>

          {/* Global search pill */}
          <div className="hidden sm:flex flex-1 max-w-2xl">
            <button onClick={() => setPaletteOpen(true)}
              className="w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-full text-left transition hover:brightness-110"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", color: "rgba(245,245,245,0.55)" }}
              data-testid="topbar-search-trigger">
              <Search size={15} color="rgba(245,245,245,0.5)" />
              <span className="flex-1 text-sm">{lang === "fr" ? "Recherche globale..." : "Global search..."}</span>
              <kbd className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(245,245,245,0.06)", color: "rgba(245,245,245,0.7)" }}>⌘K</kbd>
            </button>
          </div>
          <div className="flex-1 sm:hidden" />

          {/* Right icon row — PUNCH style */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* PROGÈRE Vision — Persistent gold CTA (always visible, all screens) */}
            <Link to="/app/quotes/new/vision" data-testid="topbar-vision-cta"
              className="inline-flex items-center gap-1.5 rounded-full font-bold whitespace-nowrap transition active:scale-95 group"
              style={{
                background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
                color: "#0B0C0F",
                padding: "7px 14px",
                fontSize: 12,
                marginRight: 4,
                boxShadow: "0 4px 12px rgba(212,175,55,0.35), 0 0 0 1px rgba(11,12,15,0.4) inset",
              }}
              title={lang === "fr" ? "Lancer PROGÈRE Vision IA" : "Launch PROGÈRE Vision AI"}>
              <Sparkles size={13} strokeWidth={2.5} className="group-hover:rotate-12 transition" />
              <span className="hidden sm:inline">{lang === "fr" ? "Vision IA" : "Vision AI"}</span>
            </Link>
            <div className="hidden md:block mr-1"><ProgereEcosystemMenu /></div>
            <TopIconBtn onClick={() => setLang(lang === "fr" ? "en" : "fr")} testid="topbar-lang" title={lang === "fr" ? "English" : "Français"}>
              <Globe size={16} />
            </TopIconBtn>
            <TopIconBtn onClick={toggleTheme} testid="topbar-theme" title="Theme">
              {theme === "dark" ? <Sun size={16} color="#D4AF37" /> : <Moon size={16} />}
            </TopIconBtn>
            <TopIconBtn onClick={toggleSound} testid="topbar-sound" title="Sound">
              {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} color="rgba(245,245,245,0.5)" />}
            </TopIconBtn>
            <button onClick={() => hasSubscription && nav("/app/erp-leads")}
              className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110 relative"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid="topbar-notif" title="Notifications">
              <Bell size={16} color="rgba(245,245,245,0.85)" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                  style={{ background: "#C00000", color: "#F5F5F5", border: "1.5px solid #0B0B0B" }}
                  data-testid="notif-badge">{unread > 9 ? "9+" : unread}</span>
              )}
            </button>

            {/* Role/plan pill (Admin gold) */}
            <div className="hidden sm:flex items-center px-3 py-1.5 rounded-full flex-shrink-0 gap-1.5"
              style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", boxShadow: "0 4px 12px rgba(212,175,55,0.35)" }}
              data-testid="topbar-role-pill">
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "#0B0B0B" }}>Admin</span>
            </div>

            <TopIconBtn onClick={() => nav("/app/settings")} testid="topbar-security" title="Sécurité">
              <ShieldCheck size={16} color="rgba(245,245,245,0.85)" />
            </TopIconBtn>

            {/* User avatar (gold pill with initials) */}
            <button onClick={() => nav("/app/settings")}
              className="w-9 h-9 rounded-full flex items-center justify-center font-black text-xs transition hover:brightness-110"
              style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0B0B", boxShadow: "0 4px 12px rgba(212,175,55,0.35)" }}
              data-testid="topbar-user-avatar" title={user?.name || user?.email}>
              {userInitials}
            </button>

            <TopIconBtn onClick={() => { logout(); nav("/"); }} testid="topbar-logout" title={t.sidebar.logout}>
              <LogOut size={16} color="rgba(245,245,245,0.85)" />
            </TopIconBtn>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8 overflow-x-hidden pb-24 lg:pb-8" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}>
          <DemoModeBanner />
          <Outlet />
        </main>
        <ProgereLegalFooter />
      </div>
      <ProgereInstallPWABanner />
      <ProgereMobileTabBar onMenuClick={() => setMobileOpen(true)} unread={unread} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AIGuide />
      <SupportBubble />
    </div>
  );
}

// Compact circular icon button for the top-right bar (PROGÈRE PUNCH style)
const TopIconBtn = ({ onClick, children, testid, title }) => (
  <button onClick={onClick} data-testid={testid} title={title} aria-label={title}
    className="w-9 h-9 rounded-full flex items-center justify-center transition hover:brightness-110"
    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
    {children}
  </button>
);
